import { useEffect, useMemo, useRef, type ReactNode } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../store/useAuthStore'
import { useTeamContext } from '@/hooks/useTeamContext'
import { routePermissions, publicRoutes, isRouteDisabledByTeamSidebar } from '@/config/permissions'
import { useDisabledSidebarItems } from '@/hooks/queries/useDisabledSidebarItems'
import { getMessageApi } from '@/utils/messageClient'
import { redirectByRole, isOnCorrectRoute } from '@/utils/redirectUtils'
import { isPostLogoutTransition } from '@/hooks/useLogout'
import { isAuthenticatedUser } from '@/lib/auth/isAuthenticated'
import { AppLoadingScreen } from '@/components/loading/AppLoadingScreen'
import { useSessionStatus, shouldShowProtectedRouteLoading } from '@/hooks/useSessionStatus'
import { useAuthStorePersistHydrated } from '@/hooks/useAuthStorePersistHydrated'
import { useAuthSessionBootstrap } from '@/hooks/queries/useAuthSessionBootstrap'
import { useSessionResume } from '@/hooks/useSessionResume'
import {
  markSessionBootstrapPerfEnd,
  markSessionBootstrapPerfStart,
} from '@/utils/bootstrapAppSession'
import { markPerformance } from '@/utils/dev/performanceMarks'

const normalizeRole = (role: string | null | undefined) =>
  role?.toString().trim().toLowerCase().replace(/_/g, '-')

function isOnboardingPath(pathname: string): boolean {
  return (
    pathname === '/company-onboarding' ||
    pathname === '/onboarding/freelancer' ||
    pathname.startsWith('/onboarding/')
  )
}

interface GuardRedirect {
  path: string
  state?: Record<string, unknown>
}

export function resolveAuthGuardRedirect(input: {
  pathname: string
  sessionHydrated: boolean
  sessionRequiredPath: string | null
  storeUser: { id?: string } | null
  user: { id?: string } | null
  impersonating: boolean
  sessionTransitioning: boolean
  sessionSilentSwap: boolean
}): GuardRedirect | null {
  const {
    pathname,
    sessionHydrated,
    sessionRequiredPath,
    storeUser,
    user,
    impersonating,
    sessionTransitioning,
    sessionSilentSwap,
  } = input

  if (!sessionHydrated) {
    const hasPendingAuth =
      isAuthenticatedUser(storeUser ?? user, sessionHydrated) ||
      impersonating ||
      sessionTransitioning ||
      sessionSilentSwap
    if (!hasPendingAuth && !pathname.startsWith('/login')) {
      return { path: '/login' }
    }
    return null
  }

  if (!isAuthenticatedUser(storeUser ?? user, sessionHydrated)) {
    if (!pathname.startsWith('/login')) {
      return { path: '/login' }
    }
    return null
  }

  if (
    sessionRequiredPath &&
    !isOnboardingPath(pathname) &&
    pathname !== sessionRequiredPath
  ) {
    return { path: sessionRequiredPath, state: { isEdit: false } }
  }

  return null
}

interface RouteGuardProps {
  children: ReactNode
}

/**
 * RouteGuard — bootstrap session (React Query) + gate auth/permissions (unique instance à la racine).
 */
export default function RouteGuard({ children }: RouteGuardProps) {
  'use no memo'
  const { sessionBootstrapped, sessionHydrated, sessionTransitioning, sessionSilentSwap, impersonating, sessionRequiredPath } =
    useAuthStore()
  const sessionStatus = useSessionStatus()
  const { user, effectiveRole, globalRole } = useTeamContext()
  const disabledSidebarItems = useDisabledSidebarItems()
  const role = globalRole === 'super-admin' ? globalRole : effectiveRole || globalRole
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const persistHydrated = useAuthStorePersistHydrated()

  useSessionResume()

  const redirectingRef = useRef(false)
  const lastRedirectRef = useRef<string | null>(null)

  const isPublic = publicRoutes.some((path) => location.pathname.startsWith(path))
  const skipBootstrap = isPostLogoutTransition() || sessionBootstrapped

  const { isPending, isFetching, isSuccess, isError } = useAuthSessionBootstrap({
    enabled: persistHydrated && !skipBootstrap,
  })

  const bootstrapInFlight = persistHydrated && !skipBootstrap && (isPending || isFetching)

  const authGuardRedirect = useMemo(() => {
    if (isPublic || isPostLogoutTransition() || sessionTransitioning || sessionSilentSwap || bootstrapInFlight) {
      return null
    }

    if (!sessionBootstrapped) {
      return null
    }

    const { user: storeUser } = useAuthStore.getState()

    return resolveAuthGuardRedirect({
      pathname: location.pathname,
      sessionHydrated,
      sessionRequiredPath,
      storeUser,
      user,
      impersonating,
      sessionTransitioning,
      sessionSilentSwap,
    })
  }, [
    isPublic,
    sessionTransitioning,
    sessionSilentSwap,
    bootstrapInFlight,
    sessionBootstrapped,
    location.pathname,
    sessionHydrated,
    sessionRequiredPath,
    user,
    impersonating,
  ])

  useEffect(() => {
    if (!persistHydrated) {
      return
    }
    markSessionBootstrapPerfStart()
    markPerformance('persist-hydrated')
  }, [persistHydrated])

  useEffect(() => {
    if (!persistHydrated) {
      return
    }
    if (skipBootstrap || isSuccess || isError) {
      markSessionBootstrapPerfEnd()
    }
  }, [persistHydrated, skipBootstrap, isSuccess, isError])

  useEffect(() => {
    if (isPublic || authGuardRedirect) {
      return
    }

    if (isPostLogoutTransition()) {
      return
    }

    if (sessionTransitioning || sessionSilentSwap) {
      return
    }

    if (bootstrapInFlight) {
      return
    }

    if (!sessionBootstrapped) {
      return
    }

    const { user: storeUser } = useAuthStore.getState()

    if (!sessionHydrated) {
      return
    }

    if (!isAuthenticatedUser(storeUser ?? user, sessionHydrated)) {
      return
    }

    if (
      sessionRequiredPath &&
      !isOnboardingPath(location.pathname) &&
      location.pathname !== sessionRequiredPath
    ) {
      return
    }

    const normalizedRole = normalizeRole(role) || normalizeRole(user?.role)
    if (!normalizedRole) {
      return
    }

    if (
      normalizedRole !== 'super-admin' &&
      isRouteDisabledByTeamSidebar(location.pathname, disabledSidebarItems)
    ) {
      const redirectKey = `disabled-sidebar-${location.pathname}`
      if (redirectingRef.current && lastRedirectRef.current === redirectKey) {
        return
      }

      redirectingRef.current = true
      lastRedirectRef.current = redirectKey

      if (!impersonating) {
        const messageApi = getMessageApi()
        if (messageApi) {
          messageApi.error(t('common.permissionDenied'))
        }
      }

      redirectByRole(navigate, normalizedRole, {}, location.pathname)

      setTimeout(() => {
        redirectingRef.current = false
      }, 1000)
      return
    }

    let routeConfig = routePermissions[location.pathname]

    if (!routeConfig) {
      const pathSegments = location.pathname.split('/').filter(Boolean)

      for (let i = pathSegments.length; i > 0; i--) {
        const testPath = '/' + pathSegments.slice(0, i).join('/')
        if (routePermissions[testPath]) {
          routeConfig = routePermissions[testPath]
          break
        }
      }
    }

    if (routeConfig?.allowedRoles) {
      const allowedRoles = routeConfig.allowedRoles.map(normalizeRole)

      if (!allowedRoles.includes(normalizedRole)) {
        if (isOnCorrectRoute(location.pathname, normalizedRole)) {
          return
        }

        const redirectKey = `${normalizedRole}-${location.pathname}`
        if (redirectingRef.current && lastRedirectRef.current === redirectKey) {
          return
        }

        redirectingRef.current = true
        lastRedirectRef.current = redirectKey

        const isSuperAdminRedirect = normalizedRole === 'super-admin'
        if (!impersonating && !isSuperAdminRedirect) {
          const messageApi = getMessageApi()
          const deniedKey = routeConfig.deniedMessageKey
          if (messageApi) {
            messageApi.error(deniedKey ? t(deniedKey) : t('common.permissionDenied'))
          }
        }

        redirectByRole(navigate, normalizedRole, {}, location.pathname)

        setTimeout(() => {
          redirectingRef.current = false
        }, 1000)
      }
    }
  }, [
    location.pathname,
    role,
    user,
    navigate,
    t,
    sessionBootstrapped,
    sessionTransitioning,
    sessionSilentSwap,
    impersonating,
    isPublic,
    bootstrapInFlight,
    authGuardRedirect,
    sessionHydrated,
    sessionRequiredPath,
    disabledSidebarItems,
  ])

  const showLoading = shouldShowProtectedRouteLoading({
    isPublic,
    persistHydrated,
    bootstrapInFlight,
    sessionTransitioning,
    sessionSilentSwap,
    sessionBootstrapped,
    sessionStatus,
  })

  if (showLoading) {
    if (isPostLogoutTransition()) {
      return null
    }
    return <AppLoadingScreen />
  }

  if (authGuardRedirect) {
    return (
      <Navigate
        to={authGuardRedirect.path}
        replace
        state={authGuardRedirect.state}
      />
    )
  }

  return children
}
